import { createContext } from "react";
import {
  AddressBookDeleteContextValue,
  AddressBookHandlerContextValue,
  AddressBookIndexContextValue,
} from "./";

/* ===========================================================================
 * CONTEXT: AddressBookIndexContext
 * ===========================================================================
 */

/**
 * @constant AddressBookIndexContext
 *
 * Provides configuration and UI-related state for the AddressBook index view.
 * Exposes:
 * - handler configuration (registry, billing, default values)
 * - modal configuration
 * - owner/service metadata
 * - optional lifecycle callback
 *
 * Consumers must use `useAddressBookIndexContext()` and must be wrapped
 * inside an `AddressBookIndexProvider`.
 */
export const AddressBookIndexContext = createContext<
  AddressBookIndexContextValue | undefined
>(undefined);

/* ===========================================================================
 * CONTEXT: AddressBookHandlerContext
 * ===========================================================================
 */

/**
 * @constant AddressBookHandlerContext
 *
 * Provides CRUD logic and state for AddressBook entities, including:
 * - loading and error handling
 * - create/update flows
 * - autosave logic
 * - billing comparison state
 * - registry-specific save functionality
 *
 * This context represents the core business logic layer for AddressBook forms.
 * Must be used through `useAddressBookHandlerContext()` and enclosed in
 * an `AddressBookHandlerProvider`.
 */
export const AddressBookHandlerContext = createContext<
  AddressBookHandlerContextValue | undefined
>(undefined);

/* ===========================================================================
 * CONTEXT: AddressBookDeleteContext
 * ===========================================================================
 */

/**
 * @constant AddressBookDeleteContext
 *
 * Provides batch deletion logic for AddressBook records:
 * - loader state
 * - multi-delete API handling
 * - callback execution on success or partial success
 *
 * Designed specifically for index/list views that support checkbox-based
 * bulk operations. Must be consumed via `useAddressBookDeleteContext()`
 * and wrapped inside an `AddressBookDeleteProvider`.
 */
export const AddressBookDeleteContext = createContext<
  AddressBookDeleteContextValue | undefined
>(undefined);
